import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate, NavLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { visuallyHidden } from "@mui/utils";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircleIcon from "@mui/icons-material/Circle";
import EnergySavingsLeafIcon from "@mui/icons-material/EnergySavingsLeaf";
import HomeIcon from "@mui/icons-material/Home";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import {
  formatOrgName,
  isSyntheticEmail,
  RolesRow,
} from "pages/components/SidebarOrgUserExtras";
import toast from "react-hot-toast";
import { useVendor } from "context/VendorContext";
import { OrgRoleScopesService } from "api/orgRoleScopes.api";
import { EnvironmentalSubscriptionsApiService } from "api/environmentalSubscriptions.api";
import { ApQueueApiService } from "afms/api/apQueue.api";
import Logo from "assets/images/logos/flex2logo.png";
import { cc } from "afms/theme/commandCenterTheme";
import {
  AFMS_SYSTEM_NAME,
  BACKOFFICE_ORG_ID,
  SYSTEM_DISPLAY_NAMES,
  getAppMode,
  getDefaultRoute,
  selectUserDetails,
  systemNameForAppMode,
} from "config/appMode";
import { setMySystemDefaultRoute } from "api/users.api";
import { getUser, logoutUser } from "redux-store/auth/actions";
import useInboxUnreadCount from "notifications/hooks/useInboxUnreadCount";
import useAwaitingPickupCount from "afms/hooks/useAwaitingPickupCount";
import { getEnvironmentalPortalRoutes } from "./environmentalNavigation";

// Nav keys (e.g. "vendor-nav-settings") are shown after each label only when asked for —
// REACT_APP_SHOW_NAV_KEYS=true or localStorage.showNavKeys = "1" — for mapping items to
// permission keys. Shown to everyone, they made labels like "Settings(vendor-nav-settings)"
// that users (and the conformance tests, TC-NAV-001/007) can't match.
const showNavKeys = (() => {
  if (process.env.REACT_APP_SHOW_NAV_KEYS === "true") return true;
  try {
    return window.localStorage.getItem("showNavKeys") === "1";
  } catch (e) {
    return false;
  }
})();

const itemSx = (active, depth) => ({
  mx: 1.25,
  mb: 0.5,
  pl: 1.4 + depth * 1.8,
  pr: 1.25,
  py: depth === 0 ? 1.05 : 0.85,
  borderRadius: "8px",
  color: active ? "#fff" : "rgba(255,255,255,0.72)",
  bgcolor: active ? cc.blue : "transparent",
  boxShadow: active ? "0 10px 20px rgba(13,110,253,0.28)" : "none",
  "&:hover": {
    bgcolor: active ? cc.blue : "rgba(13,110,253,0.18)",
    color: "#fff",
  },
  "& .MuiListItemIcon-root": {
    color: "inherit",
    minWidth: depth === 0 ? 34 : 24,
  },
});

const routePattern = (route) => {
  if (!route) return null;
  const escaped = route.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`^${escaped.replace(/:[^/]+/g, "[^/]+")}(?:/)?$`);
};

const routeMatches = (route, pathname) => {
  if (!route) return false;
  if (route.includes(":")) return routePattern(route).test(pathname);
  return pathname === route || pathname.startsWith(`${route}/`);
};

const routeHasActiveChild = (route, pathname) => {
  if (routeMatches(route.route, pathname)) return true;
  return (route.collapse || []).some((child) => routeHasActiveChild(child, pathname));
};

// A nav item with `badge: "<name>"` shows that live count beside its label.
// What each count is, for screen readers: the pill alone is just a number.
const BADGE_NOUN = { inbox: "unread", pickupsAwaiting: "waiting for confirmation" };

const routesUseBadge = (routeList, badge) =>
  routeList.some((route) => route.badge === badge || routesUseBadge(route.collapse || [], badge));

const badgeSx = {
  ml: 1,
  flexShrink: 0,
  minWidth: 18,
  height: 18,
  px: 0.6,
  borderRadius: 9,
  bgcolor: cc.red,
  color: "#fff",
  fontSize: 10,
  fontWeight: 800,
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

// Groups that should always start collapsed on page load and never be
// auto-expanded by the active-route logic. The Legacy group is parked
// reference material — promoting it to "open" on every mount makes the
// sidebar feel crowded, and auto-opening it because the user happened to
// be on a legacy route hides the fact that those pages are legacy. Users
// can still click to expand it within a session.
const ALWAYS_CLOSED_KEYS = new Set(["vendor-nav-legacy"]);

const collectActiveKeys = (routeList, pathname, keys = {}) => {
  routeList.forEach((route) => {
    const routeKey = route.key || route.name;
    if (route.collapse && routeHasActiveChild(route, pathname)) {
      if (!ALWAYS_CLOSED_KEYS.has(routeKey)) {
        keys[routeKey] = true;
      }
      // Recurse into children so descendants of an always-closed parent
      // can still be marked open if the user manually expands the parent.
      collectActiveKeys(route.collapse, pathname, keys);
    }
  });
  return keys;
};

// Sidebar group expansion is persisted in localStorage so it survives the
// sidebar being mounted afresh: on a page reload, and on mobile every time the
// navigation Drawer opens (it unmounts when closed). On desktop the shell
// mounts it once. Pages used to mount their own layout, sidebar included, so
// every navigation reset the open/closed state and unrelated groups appeared
// to collapse — the "menu mutated and I lost the section" bug.
const OPEN_KEYS_STORAGE = "afmsVendorSidebarOpenKeys";

const readStoredOpenKeys = () => {
  try {
    const raw = window.localStorage.getItem(OPEN_KEYS_STORAGE);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== "object") return {};
    // Force the always-closed groups to false on every fresh load, even
    // if localStorage has them as true from a previous session.
    const sanitized = { ...parsed };
    ALWAYS_CLOSED_KEYS.forEach((key) => {
      delete sanitized[key];
    });
    return sanitized;
  } catch (e) {
    return {};
  }
};

const writeStoredOpenKeys = (keys) => {
  try {
    window.localStorage.setItem(OPEN_KEYS_STORAGE, JSON.stringify(keys));
  } catch (e) {
    /* localStorage unavailable — expansion just won't persist across reloads */
  }
};

function EnvironmentalPortalSidebar({ onNavigate = () => {}, onSwitchSystem }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { vendorProfile, portalCapabilities, isVendorOrg } = useVendor();
  const user = useSelector((state) => state.auth.user);
  // state.auth.user has TWO shapes (see selectUserDetails); always go
  // through the normalizer so first/last/email resolve regardless of
  // whether the user just logged in (inner DTO at the top) or rehydrated
  // via getUser (outer wrapper with .userDetails inside).
  const normalizedDetails = selectUserDetails(user);

  // Whether this user processes payables (afms-api ap-queue/access), for the
  // AP queue item; null until known, which shows it.
  const [apAccess, setApAccess] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const allowed = (await ApQueueApiService.access())?.data?.allowed;
        if (!cancelled && typeof allowed === "boolean") setApAccess(allowed);
      } catch (e) {
        // Unknown: the item stays.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [normalizedDetails?.organizationId]);

  // Org-context flags used by per-item gates (e.g. requiresVendorOrg,
  // requiresEnterpriseOwner, requiresEccVisible, requiresCommandCenterVisible).
  // isEnterpriseOrganization + role come from userDetails so the Enterprise
  // menu can gate without an extra fetch. The two show* flags default to
  // true when the claim is missing (stale session / older server) so we
  // don't accidentally hide menus on a bad payload.
  const orgContext = useMemo(
    () => ({
      isVendorOrg: isVendorOrg === true,
      isEnterpriseOrganization:
        normalizedDetails?.isEnterpriseOrganization === true,
      // Managed = this org is a child of an enterprise (has an OVC edge to
      // one). Drives requiresNotManaged (e.g. hides Management Core).
      isManaged: normalizedDetails?.isManagedByEnterprise === true,
      role: normalizedDetails?.role || "",
      showEnvironmentalControlCenter:
        normalizedDetails?.showEnvironmentalControlCenter !== false,
      showCommandCenter:
        normalizedDetails?.showCommandCenter !== false,
      apAccess,
    }),
    [
      isVendorOrg,
      normalizedDetails?.isEnterpriseOrganization,
      normalizedDetails?.isManagedByEnterprise,
      normalizedDetails?.role,
      normalizedDetails?.showEnvironmentalControlCenter,
      normalizedDetails?.showCommandCenter,
      apAccess,
    ]
  );

  // Org-systems drives the "Environmental" badge under the logo. Single source
  // of truth lives on userDetails (mirrored from OrganizationSystemMappings).
  const hasEnvironmental = useMemo(() => {
    const systems = normalizedDetails?.organizationSystems;
    return Array.isArray(systems) && systems.includes(AFMS_SYSTEM_NAME);
  }, [normalizedDetails?.organizationSystems]);

  // Service manager is a SECONDARY role (scopeKind "service_manager"), carried
  // on userDetails as a plain string[]. When the user holds it, the footer
  // status chip shows their default SITE instead of "Operational".
  const isServiceManager = useMemo(
    () =>
      Array.isArray(normalizedDetails?.secondaryRoles) &&
      normalizedDetails.secondaryRoles.includes("service_manager"),
    [normalizedDetails?.secondaryRoles]
  );

  // The default-site NAME isn't on user details — it lives on the role-scope
  // membership row, so fetch it (only when the user is a service manager).
  // Best-effort: any failure leaves the chip on the "Operational" fallback.
  const [serviceManagerSite, setServiceManagerSite] = useState(null);
  // scopeKind → default location name for EVERY secondary role the user
  // holds, so each role chip in RolesRow can show its default location on
  // hover. Same OrgRoleScopesService.listMine() source as the service-
  // manager site below.
  const [roleScopeLocations, setRoleScopeLocations] = useState({});
  // The user's own current Environmental subscription (or null), for the
  // "Licence" card's licence chip. Best-effort — null renders "Unlicensed".
  const [licence, setLicence] = useState(null);
  const hasSecondaryRoles =
    Array.isArray(normalizedDetails?.secondaryRoles) &&
    normalizedDetails.secondaryRoles.length > 0;
  useEffect(() => {
    if (!isServiceManager && !hasSecondaryRoles) {
      setServiceManagerSite(null);
      setRoleScopeLocations({});
      setLicence(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = (await OrgRoleScopesService.listMine())?.data?.data ?? [];
        if (!cancelled) {
          const map = {};
          rows.forEach((r) => {
            if (r.scopeKind && r.defaultLocationName) {
              map[r.scopeKind] = r.defaultLocationName;
            }
          });
          setRoleScopeLocations(map);
          const sm = rows.find((r) => r.scopeKind === "service_manager");
          setServiceManagerSite(sm?.defaultLocationName || null);
        }
      } catch (e) {
        if (!cancelled) {
          setServiceManagerSite(null);
          setRoleScopeLocations({});
        }
      }
      // The licence card only matters for service managers.
      if (isServiceManager) {
        try {
          const res = await EnvironmentalSubscriptionsApiService.getMine();
          if (!cancelled) setLicence(res?.data?.data ?? null);
        } catch (e) {
          if (!cancelled) setLicence(null);
        }
      } else if (!cancelled) {
        setLicence(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isServiceManager, hasSecondaryRoles]);

  // Licence status light on the site chip: green = good, yellow = expiring
  // within a month, red = unlicensed. `licence` (from getMine) is only present
  // when live (trial|active, not past its period), so a null licence is red.
  // daysRemaining is 0 for a perpetual grant (no currentPeriodEnd) → green.
  const licenceState = useMemo(() => {
    if (!licence) return { light: "#ef4444", label: "Unlicensed" };
    const d = licence.daysRemaining;
    if (typeof d === "number" && d > 0 && d <= 30) {
      return { light: "#f59e0b", label: `Expiring in ${d}d` };
    }
    return {
      light: "#2ed66f",
      label: licence.status === "trial" ? "Trial (licensed)" : "Licensed",
    };
  }, [licence]);

  // Home for the logo click — defaults to the post-login landing path for
  // the user's current system. Same helper Login.js / TwoFactorAuth.js use.
  const homeRoute = useMemo(
    () => getDefaultRoute(normalizedDetails) || "/",
    [normalizedDetails]
  );

  // Gate the menu against the caller's afms portal capabilities + org
  // context (isVendorOrg). Falls back to showing every cap-gated item until
  // capabilities resolve (see getEnvironmentalPortalRoutes), but still honors the
  // org-context gates.
  const visibleRoutes = useMemo(
    () => getEnvironmentalPortalRoutes(portalCapabilities, orgContext),
    [portalCapabilities, orgContext]
  );
  // Live counts for items that declare a `badge`. Only polled while such an
  // item is actually visible; shared with the Command Center bell, so the two
  // never disagree.
  const inboxUnread = useInboxUnreadCount(routesUseBadge(visibleRoutes, "inbox"));
  const pickupsAwaiting = useAwaitingPickupCount(routesUseBadge(visibleRoutes, "pickupsAwaiting"));
  const badgeCounts = { inbox: inboxUnread, pickupsAwaiting };

  // Lazy-init from localStorage so a manually-expanded group stays expanded
  // when the sidebar mounts again (reload, or the mobile Drawer reopening).
  const [openKeys, setOpenKeys] = useState(readStoredOpenKeys);

  // Auto-open the group containing the active route — merged in, never
  // removed, so it can't collapse a group the user opened themselves.
  useEffect(() => {
    setOpenKeys((current) => ({ ...current, ...collectActiveKeys(visibleRoutes, pathname) }));
  }, [visibleRoutes, pathname]);

  // Persist expansion so it survives the sidebar remounting and page reloads.
  useEffect(() => {
    writeStoredOpenKeys(openKeys);
  }, [openKeys]);

  const handleRoute = (route) => {
    if (route.href) {
      window.open(route.href, "_blank", "noreferrer");
      return;
    }
    if (route.route) {
      navigate(route.route);
      onNavigate();
    }
  };

  const renderRoute = (route, depth = 0) => {
    if (route.type === "title") {
      return (
        <Typography
          key={route.key}
          variant="caption"
          sx={{
            display: "block",
            px: 2.5,
            pt: 2,
            pb: 0.75,
            color: "rgba(255,255,255,0.42)",
            fontSize: 10,
            fontWeight: 800,
            textTransform: "uppercase",
          }}
        >
          {route.title}
        </Typography>
      );
    }

    if (route.type === "divider") {
      return <Divider key={route.key} sx={{ borderColor: "rgba(255,255,255,0.08)", my: 1 }} />;
    }

    const key = route.key || route.name || route.route;
    const active = routeHasActiveChild(route, pathname);
    const hasChildren = route.collapse?.length > 0;
    // `noCollapse:true` + `route` is the "I am a clickable header with
    // always-visible children" pattern (matches the generic Sidenav
    // contract). The expand/collapse arrow is suppressed and the children
    // render unconditionally indented underneath. Used by EEC → Tickets.
    const noCollapse = Boolean(route.noCollapse);
    const alwaysOpen = noCollapse && hasChildren;
    const open = alwaysOpen ? true : Boolean(openKeys[key]);
    // The parent is clickable-to-navigate when (a) it has no children, or
    // (b) it has children but is marked noCollapse. In all other cases
    // clicking the parent toggles the children.
    const navigateOnClick = !hasChildren || noCollapse;
    const badgeCount = route.badge ? badgeCounts[route.badge] || 0 : 0;

    return (
      <React.Fragment key={key}>
        <ListItemButton
          selected={active}
          sx={itemSx(active, depth)}
          onClick={() => {
            if (navigateOnClick) {
              handleRoute(route);
            } else {
              setOpenKeys((current) => ({ ...current, [key]: !open }));
            }
          }}
        >
          {route.icon && <ListItemIcon>{route.icon}</ListItemIcon>}
          <ListItemText
            primary={
              <>
                {route.name}
                {showNavKeys && key ? (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.5,
                      fontFamily: "monospace",
                      fontSize: 9,
                      opacity: 0.6,
                      fontWeight: 400,
                    }}
                  >
                    ({key})
                  </Box>
                ) : null}
              </>
            }
            primaryTypographyProps={{
              fontSize: depth === 0 ? 13 : 12,
              fontWeight: active ? 800 : 600,
              noWrap: true,
            }}
          />
          {badgeCount > 0 && (
            <Box component="span" sx={badgeSx}>
              {badgeCount > 99 ? "99+" : badgeCount}
              {/* The pill alone is just a number; this makes it "3 unread". */}
              <Box component="span" sx={visuallyHidden}>
                {" "}
                {BADGE_NOUN[route.badge] || "unread"}
              </Box>
            </Box>
          )}
          {hasChildren && !noCollapse &&
            (open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
        </ListItemButton>
        {hasChildren && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List disablePadding>
              {route.collapse.map((child) => renderRoute(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  // Org name preference order: userDetails.organizationName (server-loaded
  // from Organizations.Name — works for ALL orgs) → vendorProfile (only
  // populated for vendor orgs) → legacy businessName field → label of
  // last resort. The first should hit for any logged-in user.
  const orgName =
    normalizedDetails?.organizationName ||
    vendorProfile?.organization?.name ||
    normalizedDetails?.businessName ||
    "AFMS Portal";

  const userDisplayName = (() => {
    const ud = normalizedDetails;
    // firstName/lastName live on the outer UserProfile (user), not on
    // the inner UserDetailsDto. Check both.
    const first = (ud?.firstName || user?.firstName)?.trim();
    const last = (ud?.lastName || user?.lastName)?.trim();
    const full = [first, last].filter(Boolean).join(" ");
    const realEmail = (e) => (isSyntheticEmail(e) ? null : e);
    return (
      full ||
      user?.displayName ||
      ud?.displayName ||
      user?.username ||
      ud?.username ||
      realEmail(user?.email) ||
      realEmail(ud?.email) ||
      "—"
    );
  })();

  // Logout — clear auth state and bounce to /login. dispatch awaited so
  // localStorage.authToken is gone before the navigate (no flash of
  // "you're authenticated" UI on the way out).
  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate("/login");
  };

  // "Set as my home" — pin the current pathname as the user's home for
  // the current system. Same rules as in the (removed) DashboardNavbar
  // version: hidden for backoffice (org 1) and when the current app-mode
  // doesn't map to a known system. Icon switches outlined → filled green
  // when the current pathname IS the user's pinned home.
  const userDetails = normalizedDetails;
  const currentSystemName = userDetails
    ? systemNameForAppMode(getAppMode(userDetails))
    : null;
  const pinnedRouteForSystem = currentSystemName
    ? userDetails?.systemDefaultRoutes?.[currentSystemName] || null
    : null;
  const isHomePinned = pinnedRouteForSystem === pathname;
  const showHomePinButton =
    userDetails != null &&
    userDetails.organizationId !== BACKOFFICE_ORG_ID &&
    currentSystemName != null;
  const [pinningHome, setPinningHome] = useState(false);

  const handleSetHome = async () => {
    if (pinningHome || !currentSystemName) return;
    setPinningHome(true);
    try {
      await setMySystemDefaultRoute(currentSystemName, pathname);
      await dispatch(getUser());
      const label =
        SYSTEM_DISPLAY_NAMES[getAppMode(userDetails)] || currentSystemName;
      toast.success(`Set as your ${label} home`);
    } catch (err) {
      console.error("Failed to set home route:", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Couldn't save your home page. Try again.";
      toast.error(message);
    } finally {
      setPinningHome(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: `linear-gradient(180deg, ${cc.navy} 0%, ${cc.navy2} 100%)`,
        color: "#fff",
      }}
    >
      <Box sx={{ px: 2, pt: 2.25, pb: 1.5 }}>
        {/* Logo → user's home screen. Clicking sends them back to the
            default route for their current system (computed from
            getDefaultRoute). Replaces the older logo-opens-picker behavior. */}
        <Box
          component={NavLink}
          to={homeRoute}
          onClick={onNavigate}
          sx={{
            display: "inline-block",
            textDecoration: "none",
            cursor: "pointer",
            "&:hover img": { opacity: 0.85 },
          }}
          aria-label="Go to home"
        >
          <Box
            component="img"
            src={Logo}
            alt="Flex2"
            sx={{ width: 160, maxWidth: "100%", display: "block" }}
          />
        </Box>
        {/* System name → opens the system picker so the user can switch.
            Renders as a button only when an onSwitchSystem handler is
            wired; otherwise it's a static label. */}
        {hasEnvironmental && (
          <Box
            component={onSwitchSystem ? "button" : "div"}
            type={onSwitchSystem ? "button" : undefined}
            onClick={onSwitchSystem}
            aria-label={onSwitchSystem ? "Switch to a different system" : undefined}
            sx={{
              mt: 0.75,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              color: "#2ed66f",
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              background: "none",
              border: "none",
              p: 0,
              cursor: onSwitchSystem ? "pointer" : "default",
              "&:hover": onSwitchSystem
                ? { color: "#4be287", textDecoration: "underline" }
                : undefined,
            }}
          >
            <EnergySavingsLeafIcon sx={{ fontSize: 14 }} />
            Environmental
          </Box>
        )}
      </Box>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        <List component="nav" dense disablePadding>
          {visibleRoutes.map((route) => renderRoute(route))}
        </List>
      </Box>
      <Box
        sx={{
          mx: 1.25,
          mb: 2,
          p: 1.25,
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "10px",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255,255,255,0.55)",
            fontSize: 9,
            display: "block",
            textTransform: "uppercase",
          }}
        >
          Organization
        </Typography>
        <Typography variant="body2" sx={{ color: "#fff", fontWeight: 800, fontSize: 12 }}>
          {formatOrgName(orgName, normalizedDetails)}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            color: "rgba(255,255,255,0.55)",
            fontSize: 9,
            display: "block",
            textTransform: "uppercase",
          }}
        >
          User
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
          <PersonOutlineIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.7)" }} />
          <Typography variant="body2" sx={{ color: "#fff", fontWeight: 700, fontSize: 12 }}>
            {userDisplayName}
          </Typography>
        </Box>
        <RolesRow userDetails={normalizedDetails} locationByRole={roleScopeLocations} />
        <Box sx={{ mt: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {isServiceManager ? (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                mr: 1,
                px: 1,
                py: 0.75,
                borderRadius: 1.5,
                bgcolor: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.10)",
              }}
            >
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.55)",
                  fontSize: 9,
                  display: "block",
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  mb: 0.5,
                }}
              >
                Licence
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                <Tooltip
                  title={`${serviceManagerSite || "No site"} — ${licenceState.label}`}
                  arrow
                >
                  <Chip
                    icon={<CircleIcon sx={{ color: `${licenceState.light} !important` }} />}
                    label={serviceManagerSite || "No site"}
                    size="small"
                    sx={{
                      height: 22,
                      bgcolor: "rgba(255,255,255,0.08)",
                      color: "rgba(255,255,255,0.88)",
                      fontSize: 10,
                      fontWeight: 700,
                      maxWidth: 150,
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                </Tooltip>
              </Box>
            </Box>
          ) : (
            <Chip
              icon={<CircleIcon sx={{ color: "#2ed66f !important" }} />}
              label="Operational"
              size="small"
              sx={{
                height: 22,
                bgcolor: "rgba(46,214,111,0.12)",
                color: "rgba(255,255,255,0.88)",
                fontSize: 10,
                fontWeight: 700,
              }}
            />
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            {/* Set-as-my-home: pin the current pathname as the user's
                landing route for the current system. Hidden for backoffice
                org (1) or when the current app-mode doesn't map to a known
                system. Icon turns green/filled when the current page IS
                the pinned home. */}
            {showHomePinButton ? (
              <Tooltip
                title={
                  isHomePinned
                    ? `This is your ${currentSystemName} home`
                    : `Set this page as my home for ${currentSystemName}`
                }
                placement="top"
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={handleSetHome}
                    disabled={pinningHome}
                    aria-label="Set as my home"
                    sx={{
                      color: isHomePinned ? "#2ed66f" : "rgba(255,255,255,0.65)",
                      "&:hover": { color: isHomePinned ? "#4be287" : "#fff" },
                    }}
                  >
                    {isHomePinned ? (
                      <HomeIcon fontSize="small" />
                    ) : (
                      <HomeOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <Tooltip title="Sign-in & security: password, PIN" placement="top">
              <IconButton
                size="small"
                onClick={() => navigate("/account/security")}
                aria-label="Sign-in and security"
                sx={{
                  color: "rgba(255,255,255,0.65)",
                  "&:hover": { color: "#fff" },
                }}
              >
                <KeyOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sign out" placement="top">
              <IconButton
                size="small"
                onClick={handleLogout}
                aria-label="Logout"
                sx={{
                  color: "rgba(255,255,255,0.65)",
                  "&:hover": { color: "#ff8e7a" },
                }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

EnvironmentalPortalSidebar.propTypes = {
  onNavigate: PropTypes.func,
  onSwitchSystem: PropTypes.func,
};

export default EnvironmentalPortalSidebar;
